/**
 * Module: aem-to-sb-transformer.ts
 * Description: This module is responsible for transforming AEM (Adobe Experience Manager) page data into Storybook-compatible data.
 * It processes the input AEM page data and converts it into a structured format suitable for Storybook.
 *
 * Logging: Added logging for service events and errors using `logServiceDebug` and `logError`
 * from the `logger.ts` module. This logging is intended for production debugging.
 */

import { logServiceDebug, logError } from "../helper/logger"; // Importing logger functions

interface IAemItems {
  [key: string]: {
    title: string;
    elements: any;
  };
}

interface IAemPageData {
  title: string;
  index: any;
  ":items": IAemItems;
}

interface AEMToSBTransformerArgs {
  aemPageData: IAemPageData;
}

interface ISBStory {
  story: {
    name: string;
    content: {
      Body: Array<any>;
    };
  };
}

const SERVICE_NAME = "transformAEMToSB"; // Constant for service name

export function transformAEMToSB(args: AEMToSBTransformerArgs): ISBStory {
  const { aemPageData } = args;

  logServiceDebug(SERVICE_NAME, "Starting transformation of AEM page data to Storybook format", {
    pageTitle: aemPageData?.title
  });

  try {
    const indexData = aemPageData?.index;
    const itemsKey = Object.keys(aemPageData?.[":items"] || {});
    const bodyArray = [];

    itemsKey?.forEach(itemKey => {
      const item = aemPageData[":items"][itemKey];
      let elements = item?.elements;

      if (itemKey === "pageProperties") {
        const array_meta = elements?.meta || [];
        const array_link = elements?.link || [];
        const array_script = elements?.script || [];
        const array_style = elements?.style || [];
        const seo = indexData?.seo ? [indexData?.seo] : [];

        elements = {
          ...(elements || {}),
          ...(indexData || {}),
          ...{
            seo,
            array_meta,
            array_link,
            array_script,
            array_style
          },

          type: "PAGEPROPERTY"
        };
      }
      bodyArray.push(elements);
    });

    logServiceDebug(SERVICE_NAME, "Successfully transformed AEM page data to Storybook format", {
      pageTitle: aemPageData?.title,
      bodyArrayLength: bodyArray?.length
    });

    return {
      story: {
        name: aemPageData?.title,
        content: {
          Body: bodyArray
        }
      }
    };
  } catch (error) {
    logError(SERVICE_NAME, error, {
      pageTitle: aemPageData?.title
    });
    return {
      story: {
        name: aemPageData?.title,
        content: {
          Body: []
        }
      }
    };
  }
}

export default transformAEMToSB;
